/**
 * Convierte datos en formato de filas a CSV y descarga el archivo
 * @param {Array<Array>} rows - Array de arrays donde cada sub-array es una fila
 * @param {string} filename - Nombre del archivo a descargar (sin extensión)
 * @example
 * downloadCSV([
 *   ["ID", "Nombre", "Email"],
 *   [1, "Juan", "juan@mail.com"],
 *   [2, "Ana", "ana@mail.com"]
 * ], "tabla");
 */
export const downloadCSV = (rows, filename = "datos") => {
  // Convertir las filas a formato CSV
  let csvContent = rows
    .map(row => 
      row.map(value => {
        // Escapar comillas dobles y envolver en comillas
        const stringValue = String(value ?? "");
        return `"${stringValue.replace(/"/g, '""')}"`;
      }).join(";")
    )
    .join("\n");

  // Añadir BOM para UTF-8 (ayuda a Excel a reconocer caracteres especiales)
  const blob = new Blob(
    ["\ufeff" + csvContent],
    { type: "text/csv;charset=utf-8;" }
  );

  // Crear enlace temporal y descargar
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}.csv`;
  link.click();

  // Limpiar el objeto URL
  URL.revokeObjectURL(url);
};

/**
 * Convierte un objeto de incidente a formato de fila CSV
 * @param {Object} incident - Objeto de incidente
 * @returns {Array} Array con los valores del incidente
 */
export const incidentToCSVRow = (incident) => {
  // Los minutos ya vienen del backend en minutes_consumed
  const timeInMinutes = incident.time_log?.minutes_consumed || "";

  return [
    incident.id || "",
    incident.title || "",
    incident.status || "",
    incident.assigned_to_info?.full_name || "",
    // Some callers map as agency/client, others as agency_info/client_info
    incident.agency_info?.name || incident.agency?.name || "",
    incident.client_info?.name || incident.client?.name || "",
    incident.created_by?.full_name || "",
    incident.category || "",
    timeInMinutes,
    incident.created_at ? new Date(incident.created_at).toLocaleString("es-ES") : "",
  ];
};

/**
 * Obtiene los encabezados para el CSV de incidentes
 * @returns {Array} Array con los nombres de las columnas
 */
export const getIncidentCSVHeaders = () => {
  return [
    "ID",
    "Título",
    "Estado",
    "Asignado a",
    "Agencia",
    "Cliente",
    "Creado por",
    "Categoría",
    "Tiempo (minutos)",
    "Fecha de creación",
  ];
};

export const purchaseSummaryToCSVRow = (summary) => {
  return [
    summary.agency_name || "",
    summary.total_purchased_minutes || 0,
    summary.purchases_count || 0,
  ];
};

export const getPurchaseSummaryCSVHeaders = () => {
  return [
    "Agencia",
    "Minutos comprados en rango",
    "Número de compras",
  ];
};

export const purchaseHistoryToCSVRow = (purchase) => {
  return [
    purchase.id || "",
    purchase.agency_name || "",
    purchase.purchased_minutes || 0,
    purchase.purchased_at
      ? new Date(purchase.purchased_at).toLocaleString("es-ES")
      : "",
  ];
};

export const getPurchaseHistoryCSVHeaders = () => {
  return [
    "ID compra",
    "Agencia",
    "Minutos comprados",
    "Fecha de compra",
  ];
};

const STAFF_ROLE_LABELS = {
  freelance_ticket: "Freelance",
  superadmin: "Superadmin",
};

export const staffSummaryToCSVRow = (row) => [
  row.full_name,
  row.email,
  STAFF_ROLE_LABELS[row.role] || row.role,
  row.tickets,
  row.closed_tickets,
  row.minutes_logged,
  (row.minutes_logged / 60).toFixed(1).replace(".", ","),
];

export const getStaffSummaryCSVHeaders = () => [
  "Nombre",
  "Email",
  "Rol",
  "Tickets asignados",
  "Tickets cerrados",
  "Minutos registrados",
  "Horas registradas",
];

// Each ticket behind the staff summary totals
export const staffTicketToCSVRow = (ticket, statusLabel) => [
  new Date(ticket.created_at).toLocaleDateString("es-ES"),
  `#${ticket.id}`,
  ticket.title,
  ticket.agency_name || "",
  statusLabel,
  ticket.assigned_to_name,
  ticket.minutes_logged,
];

export const getStaffTicketCSVHeaders = () => [
  "Fecha",
  "Ticket",
  "Título",
  "Agencia",
  "Estado",
  "Asignado a",
  "Minutos registrados",
];

// Primer segmento de la ruta tras /api/ -> nombre de la funcionalidad
export const LOG_MODULE_LABELS = {
  users: "Usuarios",
  agencies: "Agencias",
  clients: "Clientes",
  projects: "Proyectos",
  communication: "Comunicación",
  copilot: "Copiloto",
  dashboard: "Dashboard",
  podio: "Podio",
  leadmagnet: "Leadmagnet",
  knowledge: "Conocimiento",
  "content-generation": "Contenidos",
  reports: "Informes",
  integrations: "Integraciones",
  exports: "Exportaciones",
};

export const logToCSVRow = (log, moduleLabels = {}) => {
  return [
    log.created_at ? new Date(log.created_at).toLocaleString("es-ES") : "",
    log.user_name || "",
    log.user_email || "",
    log.agency_name || "",
    log.action || "",
    log.resource || "",
    moduleLabels[log.module] || log.module || "",
    log.path || "",
  ];
};

export const getLogCSVHeaders = () => {
  return [
    "Fecha",
    "Usuario",
    "Email",
    "Agencia",
    "Acción",
    "Recurso",
    "Funcionalidad",
    "Ruta",
  ];
};
